import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckSquare, Home, X, ArrowLeft } from 'lucide-react';
import Card from '../components/UI/Card';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { useChecklistData } from '../hooks/useChecklistData';

const Checklist = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedProperty, setSelectedProperty] = useState('');
  const [properties, setProperties] = useState([]);

  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Use the custom hook for checklist data
  const { checklistData, loading: checklistLoading, error: checklistError } = useChecklistData(selectedProperty);

  // Load properties data
  useEffect(() => {
    const loadProperties = async () => {
      try {
        const response = await fetch('/properties.json');
        const data = await response.json();
        const propertiesData = data.listings.map(listing => ({
          id: listing.id,
          name: listing.name,
          address: listing.address
        }));
        
        setProperties(propertiesData);
        
        // Check for listing parameter in URL and auto-select property
        const listingId = searchParams.get('listing');
        if (listingId) {
          setSelectedProperty(listingId);
        }
        
        setPropertiesLoading(false);
      } catch (error) {
        console.error('Failed to load properties:', error);
        setPropertiesLoading(false);
      }
    };

    loadProperties();
  }, [searchParams]);



  const openImageModal = (imageUrl, title) => {
    setSelectedImage({ url: imageUrl, title });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };



  if (propertiesLoading || checklistLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  // Show error state if checklist data failed to load
  if (checklistError && selectedProperty) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Card glass className="p-12 text-center">
            <div className="text-red-400 mb-4">
              <CheckSquare className="w-16 h-16 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('checklist.error')}</h3>
              <p className="text-gray-400">{checklistError}</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-start mb-4">
            <button
              onClick={() => navigate('/cleaning-listings')}
              className="btn-glass p-2 hover:bg-white/10 transition-colors duration-200"
              title={t('common.back', 'Back')}
            >
              <ArrowLeft className="w-6 h-6 text-purple-400" />
            </button>
          </div>
          <div className="text-center mb-4">
            <div className="flex items-center justify-center space-x-3">
              <CheckSquare className="w-8 h-8 text-purple-400" />
              <h1 className="text-3xl font-bold text-white">{t('checklist.title')}</h1>
            </div>
          </div>
          
          {/* Property Selection - Only show if no property selected via URL */}
          {!searchParams.get('listing') && (
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <select
                value={selectedProperty}
                onChange={(e) => {
                  setSelectedProperty(e.target.value);
                }}
                className="glass border border-white/20 rounded-lg px-4 py-2 text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="" className="bg-slate-800">{t('checklist.selectProperty')}</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id} className="bg-slate-800">
                    {property.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        
        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeImageModal}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={closeImageModal}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-4 rounded-b-lg">
                <h3 className="text-lg font-semibold">{selectedImage.title}</h3>
              </div>
            </div>
          </div>
        )}
          
          {/* Property Info - Show selected property name when coming from URL */}
          {searchParams.get('listing') && selectedProperty && (
            <div className="mb-6 text-center">
              <div className="text-gray-300 mb-2">
                <span className="text-lg">
                  {properties.find(p => p.id.toString() === selectedProperty)?.name || 'Property'}
                </span>
              </div>
              <div className="text-gray-400 text-sm">
                {properties.find(p => p.id.toString() === selectedProperty)?.address || ''}
              </div>
            </div>
          )}


        </div>

        {selectedProperty ? (
          <div className="space-y-8">
            {/* Task Categories */}
            {checklistData.categories.map((category) => (
              <Card key={category.id} glass className="p-6">
                <div className="flex items-center space-x-3 mb-6">
                  <span className="text-2xl">{category.icon}</span>
                  <h2 className="text-2xl font-bold text-white">{category.name}</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {category.tasks.map(task => (
                    <Card
                      key={task.id}
                      className="p-4 glass border-white/10"
                    >
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-white">
                            {task.title}
                          </h3>
                        </div>
                        
                        <p className="text-gray-400 text-sm whitespace-pre-line">{task.description}</p>
                        
                        <img
                          src={task.imageUrl}
                          alt={task.title}
                          className="w-full h-48 rounded-lg object-cover cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => openImageModal(task.imageUrl, task.title)}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>
            ))}


          </div>
        ) : (
          <Card glass className="p-12 text-center">
            <Home className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">{t('checklist.selectProperty')}</h3>
            <p className="text-gray-400">{t('checklist.chooseProperty')}</p>
          </Card>
        )}
      </div>


    </div>
  );
};

export default Checklist;
